// src/pages/EditPaciente.jsx

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import axiosInstance from '../axiosInstance';
import { toast } from 'react-toastify';

// Importaciones de MUI
import {
  Paper, Box, Typography, TextField, Button, FormControl,
  InputLabel, Select, MenuItem, Avatar, IconButton, CircularProgress
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Helpers
const isCancel = (e) =>
  e?.name === 'CanceledError' ||
  e?.name === 'AbortError' ||
  e?.code === 'ERR_CANCELED' ||
  e?.message === 'canceled';

// Convierte a URL absoluta si viene relativa (/media/..)
const toAbsoluteUrl = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url; // ya es absoluta
  const base = (import.meta.env.VITE_API_URL || window.location.origin).replace(/\/+$/, '');
  const path = url.startsWith('/') ? url : `/${url}`;
  return `${base}${path}`;
};

const EditPaciente = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [pacienteData, setPacienteData] = useState({
    nombres: '',
    apellidos: '',
    fecha_nacimiento: '',
    sexo: 'Masculino',
    dni: '',
    email: '',
    profesion: '',
    grupo_sanguineo: '',
    residencia: '',
    observaciones: '',
    telefono_principal: '',
    foto_perfil: null
  });

  const [previewImage, setPreviewImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingImage, setLoadingImage] = useState(false);

  // Lista de grupos sanguíneos para el dropdown
  const gruposSanguineos = [
    'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'
  ];

  useEffect(() => {
    const ctrl = new AbortController();

    const loadPaciente = async () => {
      setLoading(true);
      try {
        const { data } = await axiosInstance.get(`/pacientes/${id}/`, { signal: ctrl.signal });
        setPacienteData(prev => ({
          ...prev,
          ...data,
          sexo: (data.sexo && data.sexo.trim()) ? data.sexo : (prev.sexo || 'Masculino'),
        }));
        if (data.foto_perfil) {
          setPreviewImage(toAbsoluteUrl(data.foto_perfil));
        }
      } catch (error) {
        if (isCancel(error)) return;
        toast.error("No se pudieron cargar los datos del paciente.");
        console.error('Error paciente:', error);
      } finally {
        setLoading(false);
      }
    };

    if (!id || id === 'undefined') {
      toast.error("ID de paciente no válido.");
      navigate(-1);
      return;
    }

  loadPaciente();
  return () => ctrl.abort();
}, [id, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPacienteData(prevState => ({ ...prevState, [name]: value }));
  };

  const compressImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const maxWidth = 600;
          const maxHeight = 600;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          }, 'image/jpeg', 0.5);
        };
      };
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setLoadingImage(true);
      const compressedFile = await compressImage(file);

      // Si aún es > 1MB, intentar otra pasada
      let finalFile = compressedFile;
      if (compressedFile.size > 1 * 1024 * 1024) {
        finalFile = await compressImage(compressedFile);
      }

      // Crear URL de vista previa (revoca anterior si era ObjectURL)
      if (previewImage && previewImage.startsWith('blob:')) {
        URL.revokeObjectURL(previewImage);
      }
      const previewUrl = URL.createObjectURL(finalFile);
      setPreviewImage(previewUrl);

      setPacienteData(prevState => ({ ...prevState, foto_perfil: finalFile }));
      toast.success("Imagen procesada correctamente");
    } catch (error) {
      console.error("Error al comprimir la imagen:", error);
      toast.error("Error al procesar la imagen");
    } finally {
      setLoadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();

    // Helper para no mandar strings vacíos
    const appendIfUseful = (key, val) => {
      if (val === null || val === undefined) return;
      if (typeof val === 'string' && val.trim() === '') return; // evita ""
      formData.append(key, val);
    };

    // Garantizar sexo válido siempre
    const sexoValue = pacienteData.sexo && pacienteData.sexo.trim() ? pacienteData.sexo : 'Masculino';

    Object.keys(pacienteData).forEach((key) => {
      const val = pacienteData[key];

      if (key === 'foto_perfil' && typeof val !== 'string') {
        if (val) formData.append(key, val);
        return;
      }

      if (key === 'sexo') {
        appendIfUseful('sexo', sexoValue);
        return;
      }

      appendIfUseful(key, val);
    });

    try {
      await axiosInstance.patch(
        `/pacientes/${id}/`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          transformRequest: (data) => data,
        }
      );
      toast.success('¡Paciente actualizado con éxito!');
      navigate(`/pacientes/${id}`);
    } catch (error) {
      console.error('Error al actualizar:', error.response?.data || error);
      // Muestra el detalle si viene del backend
      const detail = error.response?.data;
      if (detail && typeof detail === 'object') {
        const firstError = Object.entries(detail)[0];
        if (firstError) {
          const [field, msgs] = firstError;
          const msg = Array.isArray(msgs) ? msgs[0] : JSON.stringify(msgs);
          toast.error(`${field}: ${msg}`);
        } else {
          toast.error('Hubo un error al actualizar el paciente.');
        }
      } else {
        toast.error('Hubo un error al actualizar el paciente.');
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '80vh'
      }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
          Cargando datos del paciente...
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={4} sx={{ maxWidth: '800px', margin: '0 auto' }}>
      {/* Botón para volver */}
      <Button
        variant="outlined"
        component={RouterLink}
        to={`/pacientes/${id}`}
        startIcon={<ArrowBackIcon />}
        sx={{
          mb: 4,
          borderRadius: '8px',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateX(-5px)'
          }
        }}
      >
        Volver al Paciente
      </Button>

      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 4,
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}
      >
        <Typography variant="h5" component="h1" gutterBottom fontWeight="bold">
          Editar Paciente
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Sección para la foto de perfil */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar
                src={previewImage}
                sx={{
                  width: 120,
                  height: 120,
                  bgcolor: pacienteData.sexo === 'Masculino' ? 'primary.main' : 'secondary.main',
                  fontSize: '2.5rem',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                }}
              >
                {pacienteData.nombres?.charAt(0)}{pacienteData.apellidos?.charAt(0)}
              </Avatar>
              <input
                accept="image/*"
                type="file"
                id="foto-perfil-input"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <label htmlFor="foto-perfil-input">
                <IconButton
                  component="span"
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    }
                  }}
                >
                  <PhotoCameraIcon />
                </IconButton>
              </label>
              {loadingImage && (
                <Box sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(0,0,0,0.3)',
                  borderRadius: '50%'
                }}>
                  <CircularProgress size={28} sx={{ color: 'white' }} />
                </Box>
              )}
            </Box>
          </Box>

          {/* Información básica */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <TextField
              label="Nombres"
              name="nombres"
              value={pacienteData.nombres || ''}
              onChange={handleChange}
              required
              fullWidth
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
            <TextField
              label="Apellidos"
              name="apellidos"
              value={pacienteData.apellidos || ''}
              onChange={handleChange}
              required
              fullWidth
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
            <TextField
              label="Fecha de Nacimiento"
              type="date"
              name="fecha_nacimiento"
              value={pacienteData.fecha_nacimiento || ''}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              required
              fullWidth
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
            <FormControl fullWidth variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
              <InputLabel>Sexo</InputLabel>
              <Select
                name="sexo"
                value={pacienteData.sexo || 'Masculino'}
                label="Sexo"
                onChange={handleChange}
              >
                <MenuItem value="Masculino">Masculino</MenuItem>
                <MenuItem value="Femenino">Femenino</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="DNI"
              name="dni"
              value={pacienteData.dni || ''}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
            <TextField
              label="Email"
              name="email"
              type="email"
              value={pacienteData.email || ''}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
            <TextField
              label="Teléfono"
              name="telefono_principal"
              value={pacienteData.telefono_principal || ''}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
            <TextField
              label="Profesión"
              name="profesion"
              value={pacienteData.profesion || ''}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
            <FormControl fullWidth variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
              <InputLabel>Grupo Sanguíneo</InputLabel>
              <Select
                name="grupo_sanguineo"
                value={pacienteData.grupo_sanguineo || ''}
                label="Grupo Sanguíneo"
                onChange={handleChange}
              >
                <MenuItem value=""><em>No especificado</em></MenuItem>
                {gruposSanguineos.map(tipo => (
                  <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Lugar de Residencia"
              name="residencia"
              value={pacienteData.residencia || ''}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
          </Box>

          {/* Observaciones */}
          <TextField
            label="Observaciones"
            name="observaciones"
            value={pacienteData.observaciones || ''}
            onChange={handleChange}
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />

          {/* Botones de acción */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate(`/pacientes/${id}`)}
              sx={{ borderRadius: '8px' }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                borderRadius: '8px',
                px: 3,
                py: 1.2,
                boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                }
              }}
            >
              Guardar Cambios
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default EditPaciente;
